import React, { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Search, X } from "lucide-react";
import { DocumentServices } from "../../services/DocumentService";
import { RepresentanteService } from "../../services/RepresentanteService";
import { ClienteService } from "../../services/ClienteService";

const ModalRepresentantes = ({
  clientesSeleccionados,
  onGuardar,
  onCerrar,
  token,
  numero,
  tipoDocumento,
}) => {
  const [clientes, setClientes] = useState(clientesSeleccionados);
  const [nuevoCliente, setNuevoCliente] = useState({
    id: "",
    nombre: "",
    ap_paterno: "",
    ap_materno: "",
    id_tipo_documento: "",
    numero_documento: "",
    telefono: "",
    email: "",
    existente: false
  });
  const [tiposDocumentos, setTiposDocumentos] = useState([]);
  const verificacionHecha = useRef(false);
  const [errors, setErrors] = useState({});

  const agregarCliente = () => {
    if (!validarCampos()) return;

    setClientes([...clientes, nuevoCliente]);
    setNuevoCliente({
      id: "",
      nombre: "",
      ap_paterno: "",
      ap_materno: "",
      id_tipo_documento: "",
      numero_documento: "",
      telefono: "",
      email: "",
      existente: false
    });
    setErrors({});
  };

  const validarCampos = () => {
    const nuevosErrores = {};

    if (!nuevoCliente.id_tipo_documento) {
      nuevosErrores.id_tipo_documento = "El tipo de documento es obligatorio";
    }

    if (!nuevoCliente.numero_documento || !/^\d+$/.test(nuevoCliente.numero_documento)) {
      nuevosErrores.numero_documento =
        "El número es obligatorio y debe contener solo dígitos";
    }

    if (!nuevoCliente.nombre.trim()) {
      nuevosErrores.nombre = "El nombre es obligatorio";
    }

    if (!nuevoCliente.ap_paterno.trim()) {
      nuevosErrores.ap_paterno = "El apellido paterno es obligatorio";
    }

    if (nuevoCliente.telefono && !/^\d{7,15}$/.test(nuevoCliente.telefono)) {
      nuevosErrores.telefono = "El teléfono debe tener entre 7 y 15 dígitos";
    }

    if (nuevoCliente.email) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

      if (!emailRegex.test(nuevoCliente.email)) {
        nuevosErrores.email = "El correo electrónico debe tener formato válido";
      }
    }

    setErrors(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const cargarTipoDocumentos = useCallback(async () => {
    try {
      const response = await DocumentServices.getDocuments(token);
      // Filtrar los que NO contienen "RUC"
      const documentosFiltrados = (response || []).filter(
        (doc) =>
          doc.description && !doc.description.toUpperCase().includes("RUC")
      );

      setTiposDocumentos(documentosFiltrados);
    } catch (error) {
      console.error("Error al cargar tipos de Documentos:", error);
    }
  }, [token]);

  const verificarRepresentanteExistente = useCallback(async () => {
    try {
      if (!numero || !tipoDocumento) return;

      const data = await RepresentanteService.verificarExistencia({ token, numero, tipoDocumento });

      if (data.existe && data.representantes?.length > 0) {
        const nuevosRepresentante = [
          ...(nuevoCliente?.numero ? [nuevoCliente] : []),
          ...data.representantes.map((r) => ({
            id: r.id,
            nombre: r.nombre,
            ap_paterno: r.ap_paterno,
            ap_materno: r.ap_materno,
            id_tipo_documento: r.id_tipo_documento,
            numero_documento: r.numero_documento,
            telefono: r.telefono || "",
            email: r.email || "",
            existente: r.existente,
          })),
        ];

        setClientes(prev => [...prev, ...nuevosRepresentante]);
      }
    } catch (error) {
      console.error("Error al verificar representante:", error);
    }
  }, [numero, tipoDocumento, token]);

  useEffect(() => {
    cargarTipoDocumentos();
    if (clientesSeleccionados.length < 1 && !verificacionHecha.current) {
      verificacionHecha.current = true;
      verificarRepresentanteExistente();
    }
  }, [cargarTipoDocumentos, clientesSeleccionados.length, verificarRepresentanteExistente]);

  const eliminarCliente = (index) => {
    const nuevosClientes = [...clientes];
    nuevosClientes.splice(index, 1);
    setClientes(nuevosClientes);
  };

  const buscarCliente = async () => {
    try {
      const numeroDocumento = nuevoCliente.numero_documento;
      const tipoDocumento = nuevoCliente.id_tipo_documento;

      if (!numeroDocumento || numeroDocumento.trim() === "") {
        setErrors((prev) => ({
          ...prev,
          numero_documento: "Ingrese un número válido",
        }));
        return;
      }

      if (tipoDocumento === "1") {
        const cliente = await ClienteService.buscarPorDNI({ token, numero: numeroDocumento });

        if (cliente) {
          setNuevoCliente((prev) => ({
            ...prev,
            nombre: cliente.nombres || "",
            apellidoMaterno: cliente.apellido_materno || "",
            apellidoPaterno: cliente.apellido_paterno || "",
          }));

          setErrors((prev) => ({
            ...prev,
            numero_documento: null,
          }));
        }
      }
    } catch (err) {
      const mensaje = err.response?.data?.error || err.message || "Error al consultar el servicio";
      setErrors((prev) => ({
        ...prev,
        numero_documento: mensaje,
      }));
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <div className="modal-header">
          <span>Datos Representantes</span>
          <button className="close-btn" onClick={onCerrar}>
            ×
          </button>
        </div>
        <div className="modal-content">
          <div className="form-grid">
            <div className="">
              <label htmlFor="id_tipo_documento">Tipo de Documento *</label>
              <select
                name="id_tipo_documento"
                value={nuevoCliente.id_tipo_documento}
                onChange={(e) =>
                  setNuevoCliente({
                    ...nuevoCliente,
                    id_tipo_documento: e.target.value,
                  })
                }
              >
                <option value="">Selecciona tipo de documento</option>
                {tiposDocumentos.map((tipo) => (
                  <option key={tipo.id} value={tipo.id}>
                    {tipo.description}
                  </option>
                ))}
              </select>
              {errors.id_tipo_documento && (
                <span className="error">{errors.id_tipo_documento}</span>
              )}
            </div>
            <div>
              <label htmlFor="numero_documento">Número</label>
              <div className="input-with-button">
                <input
                  name="numero_documento"
                  value={nuevoCliente.numero_documento}
                  onChange={(e) =>
                    setNuevoCliente({ ...nuevoCliente, numero_documento: e.target.value })
                  }
                  placeholder="Solo números"
                />
                {nuevoCliente.id_tipo_documento === "1" && (
                  <button
                    type="button"
                    className="search-btn-documento"
                    title="Buscar cliente"
                    onClick={buscarCliente}
                  >
                    <Search size={20} color="#555" />
                  </button>
                )}
              </div>
              {errors.numero_documento && <span className="error">{errors.numero_documento}</span>}
            </div>
          </div>
          <div className="form-grid">
            <div>
              <label htmlFor="nombre">Nombres *</label>
              <div className="input-with-button">
                <input
                  type="text"
                  name="nombre"
                  value={nuevoCliente.nombre}
                  onChange={(e) =>
                    setNuevoCliente({ ...nuevoCliente, nombre: e.target.value })
                  }
                  placeholder="Nombres del Representante"
                />
              </div>
              {errors.nombre && <span className="error">{errors.nombre}</span>}
            </div>
            <div>
              <label htmlFor="ap_paterno">Apellido Paterno *</label>
              <div className="input-with-button">
                <input
                  type="text"
                  name="ap_paterno"
                  value={nuevoCliente.ap_paterno}
                  onChange={(e) =>
                    setNuevoCliente({
                      ...nuevoCliente,
                      ap_paterno: e.target.value,
                    })
                  }
                  placeholder="Apellido paterno"
                />
              </div>
              {errors.ap_paterno && (
                <span className="error">{errors.ap_paterno}</span>
              )}
            </div>
          </div>
          <div className="form-grid">
            <div>
              <label htmlFor="ap_materno">Apellido Materno </label>
              <div className="input-with-button">
                <input
                  type="text"
                  name="ap_materno"
                  value={nuevoCliente.ap_materno}
                  onChange={(e) =>
                    setNuevoCliente({
                      ...nuevoCliente,
                      ap_materno: e.target.value,
                    })
                  }
                  placeholder="Apellido paterno"
                />
              </div>
              {errors.ap_materno && (
                <span className="error">{errors.ap_materno}</span>
              )}
            </div>
            <div>
              <label htmlFor="telefono">Teléfono de Contacto </label>
              <div className="input-with-button">
                <input
                  type="text"
                  name="telefono"
                  value={nuevoCliente.telefono}
                  onChange={(e) =>
                    setNuevoCliente({
                      ...nuevoCliente,
                      telefono: e.target.value,
                    })
                  }
                  placeholder="Teléfono de Contacto"
                />
              </div>
              {errors.telefono && (
                <span className="error">{errors.telefono}</span>
              )}
            </div>
          </div>
          <div className="form-grid">
            <div>
              <label htmlFor="email">Correo electrónico </label>
              <div className="input-with-button">
                <input
                  type="text"
                  name="email"
                  value={nuevoCliente.email}
                  onChange={(e) =>
                    setNuevoCliente({
                      ...nuevoCliente,
                      email: e.target.value,
                    })
                  }
                  placeholder="Correo electrónico"
                />
              </div>
              {errors.email && <span className="error">{errors.email}</span>}
            </div>
          </div>
          <button type="button" onClick={agregarCliente} className="save-btn">
            Agregar
          </button>
          <div style={{ marginTop: "20px" }}>
            <table className="usuarios-table">
              <thead>
                <tr>
                  <th>Tipo de Documento</th>
                  <th>Numero</th>
                  <th>Nombre</th>
                  <th>Apellido Paterno</th>
                  <th>Apellido Materno</th>

                  <th>Email</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {clientes.map((cliente, index) => (
                  <tr key={cliente.numero_documento || index}>
                    <td>{cliente.id_tipo_documento}</td>
                    <td>{cliente.numero_documento}</td>
                    <td>{cliente.nombre}</td>
                    <td>{cliente.ap_paterno}</td>
                    <td>{cliente.ap_materno}</td>

                    <td>{cliente.email}</td>
                    <td>
                      {!cliente.existente && (
                        <button
                          className="delete-btn"
                          onClick={() => eliminarCliente(index)}
                        >
                          <X size={18} color="#e74c3c" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={() => onGuardar(clientes)} className="save-btn">
            Terminar
          </button>
        </div>
      </div>
    </div>
  );
};

ModalRepresentantes.propTypes = {
  clientesSeleccionados: PropTypes.array,
  onGuardar: PropTypes.func.isRequired,
  onCerrar: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  numero: PropTypes.string,
  tipoDocumento: PropTypes.string,
};

export default ModalRepresentantes;
